import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronRight, HelpCircle } from "lucide-react";
import { Button } from "../../../components/ui/primitives";

export function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isHome = pathname === "/";
  const isSmartInsightsActive = pathname.startsWith("/smart-insights");
  const isQuantFlowsActive = pathname.startsWith("/my-quant-flows");
  const isStrategies = pathname === "/strategies";

  const isLight = isHome || isSmartInsightsActive || isQuantFlowsActive;

  const theme = isStrategies
    ? {
        text: "text-gray-900",
        textSecondary: "text-gray-600",
        textTertiary: "text-gray-400",
        hoverBg: "hover:bg-black/5",
        hoverText: "hover:text-gray-900",
        border: "border-black/10",
        bgSurface: "bg-black/5",
        buttonGhost: "!text-gray-600 hover:!text-gray-900 hover:!bg-black/5",
      }
    : isLight
    ? {
        text: "text-slate-900",
        textSecondary: "text-slate-600",
        textTertiary: "text-slate-400",
        hoverBg: "hover:bg-slate-50",
        hoverText: "hover:text-slate-900",
        border: "border-slate-200",
        bgSurface: "bg-white",
        buttonGhost: "!text-slate-500 hover:!text-slate-900 hover:!bg-slate-50",
      }
    : {
        text: "text-white",
        textSecondary: "text-white/70",
        textTertiary: "text-white/40",
        hoverBg: "hover:bg-white/5",
        hoverText: "hover:text-white",
        border: "border-white/10",
        bgSurface: "bg-white/5",
        buttonGhost: "",
      };

  return (
    <header className="flex h-16 items-center justify-between">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          {/* <LogoMark mode={isStrategies ? "strategies" : isLight ? "light" : "dark"} /> */}
          <Link
            to="/"
            aria-label="Go to home"
            className={`text-base font-semibold tracking-tight ${theme.text} rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40 hover:opacity-90`}
          >
            K-Quant
          </Link>
        </div>

        <nav className="hidden items-center gap-3 md:flex">
          <Button
            variant={isSmartInsightsActive ? "primary" : "ghost"}
            size="sm"
            className={`h-8 px-4 ${!isSmartInsightsActive ? theme.buttonGhost : ""}`}
            onClick={() => navigate("/smart-insights")}
          >
            Smart Insights
          </Button>

          <Button
            variant={isQuantFlowsActive ? "primary" : "ghost"}
            size="sm"
            className={`h-8 px-4 ${!isQuantFlowsActive ? theme.buttonGhost : ""}`}
            onClick={() => navigate("/my-quant-flows")}
          >
            Quant flows
            {/* {isQuantFlowsActive ? null : (
              <ChevronRight className={`h-3.5 w-3.5 ${theme.textTertiary}`} />
            )} */}
          </Button>

          {/* <NavItem label="Quant square" theme={theme} />
          <NavItem label="Back testing" theme={theme} /> */}
        </nav>
      </div>


      <div className="flex items-center gap-3">
        <button
          type="button"
          className={`hidden items-center gap-2 text-xs ${theme.textSecondary} ${theme.hoverText} md:flex`}
        >
          <HelpCircle className="h-4 w-4" />
          Help center
        </button>

        <Button
          variant="ghost"
          size="icon"
          className={`rounded-full ${theme.buttonGhost}`}
        >
          <Bell className={`h-4 w-4 ${theme.textSecondary}`} />
        </Button>
        <div
          className={`grid h-9 w-9 place-items-center rounded-full border ${theme.border} ${theme.bgSurface} text-sm font-semibold ${theme.text}`}
        >
          NA
        </div>
      </div>
    </header>
  );
}

function NavItem({ label, theme }: { label: string; theme: any }) {
  return (
    <button
      type="button"
      className={`flex items-center gap-1 rounded-full px-3 py-2 text-xs ${theme.textSecondary} ${theme.hoverBg} ${theme.hoverText}`}
    >
      {label}
      <ChevronRight className={`h-3.5 w-3.5 ${theme.textTertiary}`} />
    </button>
  );
}


function LogoMark({ mode }: { mode?: "dark" | "strategies" | "light" }) {
  const m = mode ?? "dark";
  return (
    <div className="relative grid h-8 w-8 place-items-center">
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-400/40 to-blue-500/30 blur-[1px]" />
      <div
        className={`relative h-7 w-7 rounded-lg border ${
          m === "strategies"
            ? "border-black/10 bg-black/5"
            : m === "light"
            ? "border-slate-200 bg-white"
            : "border-white/10 bg-white/5"
        }`}
      >
        <div className="absolute left-[10px] top-[7px] h-[14px] w-[2px] bg-cyan-300/80" />
        <div className="absolute left-[13px] top-[7px] h-[14px] w-[2px] bg-blue-400/70" />
        <div className="absolute left-[16px] top-[7px] h-[14px] w-[2px] bg-indigo-400/60" />
      </div>
    </div>
  );
}
